import { useEffect, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";

import stepBack from "@assets/images/stepsBack.jpg";
import { placesSliderData, sliderData } from "data/uiData/RanksData";
import { fetchAllRanks } from "api/quizGame";
import ActualDietPlanLoader from "@components/ui/Loaders/ActualDietPlanLoader";
import { LockSleepCoach } from "@utils/defaultImages";
import Card from "@mui/material/Card";
import { ChevronLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const AllRanks = () => {
  const [firstLoad, setFirstLoad] = useState(true);
  const [badgeAchived, setBadgeAchived] = useState(null);
  const [placesAchived, setPlacesAchived] = useState(null);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const fetchRanksData = async () => {
    try {
      const response = await fetchAllRanks();
      const data = response.data;
      setBadgeAchived(data?.milestones_achieved);
      setPlacesAchived(data?.places_achieved);
    } catch (error) {
      console.log("error getting ranks:", error);
    } finally {
      setFirstLoad(false);
    }
  };

  useEffect(() => {
    fetchRanksData();
  }, []);

  // Separate achieved and unachieved milestones
  const achievedMilestones = sliderData?.filter(
    (data) => badgeAchived?.[data.title],
  );
  const unachievedMilestones = sliderData?.filter(
    (data) => !badgeAchived?.[data.title],
  );

  // Separate achieved and unachieved places
  const achievedPlaces = placesSliderData?.filter(
    (data) => placesAchived?.[data.title],
  );
  const unachievedPlaces = placesSliderData?.filter(
    (data) => !placesAchived?.[data.title],
  );

  const renderMilestoneSlider = (data, isAchieved, title, uniqueId) => (
    <div
      className="
    mb-6
    [@media(max-width:639px)]:!max-w-[70vw]
    sm:max-w-[100%]
  "
    >
      <h5 className="text-[16px] font-[Lato] text-white mb-3 ml-2">{title}</h5>
      <Swiper
        modules={[Navigation]}
        spaceBetween={20}
        slidesPerView={1}
        // This ensures the swiper container allows slides to stretch
        className="all-meals !px-[22px] sm:!px-[20px] !py-[25px] rounded-[10px] relative h-full"
        breakpoints={{
          0: { slidesPerView: 1 },
          700: { slidesPerView: 2, spaceBetween: 15 },
          1024: { slidesPerView: 2, spaceBetween: 20 },
          1280: { slidesPerView: 3, spaceBetween: 20 },
        }}
        navigation={{
          nextEl: `.swiper-button-next-${uniqueId}`,
          prevEl: `.swiper-button-prev-${uniqueId}`,
        }}
      >
        <div
          className={`swiper-button-prev-${uniqueId} swiper-button-prev after:!text-[20px] after:!font-[600] !bg-white !rounded-full !w-9 !h-9 sm:!w-10 sm:!h-10 !flex !items-center !justify-center !shadow-md after:!text-[#0575e6] absolute top-1/2 !-left-0 z-10`}
        ></div>
        <div
          className={`swiper-button-next-${uniqueId} swiper-button-next after:!text-[20px] after:!font-[600] !bg-white !rounded-full !w-9 !h-9 sm:!w-10 sm:!h-10 !flex !items-center !justify-center !shadow-md after:!text-[#0575e6] absolute top-1/2 !-right-0 z-10`}
        ></div>
        {data?.map((item, index) => {
          const isLocked = badgeAchived?.[item.title];
          const unlockDate = badgeAchived?.[item.title]?.unlockDate || null;

          return (
            /* 1. Ensure SwiperSlide takes full height of the track */
            <SwiperSlide key={index} className="!h-auto">
              {/* 2. flex-col and h-full on the wrapper makes the card stretch */}
              <div className="flex flex-col justify-center w-full h-full relative">
                <div
                  className={`${
                    !isLocked && "blur-[8px]"
                  } bg-white rounded-3xl w-full max-w-[80%] p-5 relative pb-8 mx-auto flex flex-col h-full`}
                >
                  {/* Content Container */}
                  <div className="flex-grow flex flex-col justify-between">
                    <div className="flex justify-center">
                      <h3 className="text-[#000] text-[12px] sm:text-[16px] font-[600] font-[Lato] mt-[30%] text-center">
                        {t(`quizGame.AllRanks.${item?.title}.title`)}
                      </h3>
                    </div>

                    <div className="flex justify-center">
                      {isLocked && (
                        <button
                          disabled
                          className="bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] rounded-[22px] py-2 px-3 text-[10px] sm:text-[12px] my-2 text-white"
                        >
                          {!unlockDate
                            ? t("quizGame.Locked")
                            : `${t("quizGame.Unlocked")} on ${new Date(unlockDate).getDate()} ${t(`month.${new Date(unlockDate).toLocaleDateString("en-US", { month: "long" })}`)}`}
                        </button>
                      )}
                    </div>

                    {/* This description box will now align even if text is short */}
                    <div className="border border-[#DDDDDD] p-2 rounded-xl mb-auto">
                      <p className="text-[10px] md:text-[12px] text-[#5C5C5C] text-center">
                        {t(`quizGame.AllRanks.${item?.title}.description`)}
                      </p>
                    </div>
                  </div>

                  <img
                    src={item.RankImage}
                    alt="rank"
                    className="absolute -top-[8%] left-1/2 -translate-x-1/2 z-[9] w-[40%] object-cover"
                  />
                </div>

                {!isLocked && (
                  <img
                    src={LockSleepCoach}
                    alt="locked"
                    className="absolute top-[40%] 2xl:top-[30%] left-1/2 -translate-x-1/2 z-[10] object-cover w-[40px] sm:w-[50px]"
                  />
                )}
              </div>
            </SwiperSlide>
          );
        })}
      </Swiper>
    </div>
  );

  const renderPlacesSlider = (data, isAchieved, title, uniqueId) => (
    <div
      className="
    mb-6
    [@media(max-width:639px)]:!max-w-[70vw]
    sm:max-w-[100%]
  "
    >
      <h5 className="text-[16px] font-[Lato] text-white mb-3 ml-2">{title}</h5>
      <Swiper
        modules={[Navigation]}
        spaceBetween={20}
        slidesPerView={1}
        breakpoints={{
          0: {
            slidesPerView: 1,
          },
          700: {
            slidesPerView: 2,
            spaceBetween: 15,
          },
          1024: {
            slidesPerView: 2,
            spaceBetween: 20,
          },
          1280: {
            slidesPerView: 3,
            spaceBetween: 20,
          },
        }}
        // loop={data?.length > 1}
        navigation={{
          nextEl: `.swiper-button-next-${uniqueId}`,
          prevEl: `.swiper-button-prev-${uniqueId}`,
        }}
        className="all-meals !px-[22px] sm:!px-[20px] !py-[25px] rounded-[10px] relative"
      >
        {/* Custom navigation buttons */}
        <div
          className={`swiper-button-prev-${uniqueId} swiper-button-prev after:!text-[20px] after:!font-[600] !bg-white !rounded-full !w-9 !h-9 sm:!w-10 sm:!h-10 !flex !items-center !justify-center !shadow-md after:!text-[#0575e6] absolute top-1/2 !-left-0 z-10`}
        ></div>
        <div
          className={`swiper-button-next-${uniqueId} swiper-button-next after:!text-[20px] after:!font-[600] !bg-white !rounded-full !w-9 !h-9 sm:!w-10 sm:!h-10 !flex !items-center !justify-center !shadow-md after:!text-[#0575e6] absolute top-1/2 !-right-0 z-10`}
        ></div>

        {data?.map((item, index) => {
          const isLocked = placesAchived?.[item.title];
          const unlockDate = placesAchived?.[item.title]?.unlockDate || null;

          return (
            <SwiperSlide key={index}>
              <div className="flex justify-center w-full h-full relative">
                <div
                  className={`${
                    !isLocked && "blur-[8px]"
                  } bg-white rounded-3xl w-full max-w-[80%] min-h-[320px] p-5 relative pb-8 flex flex-col h-[320px]`}
                >
                  <img
                    src={item.RankImage}
                    alt="image"
                    className={`w-full object-cover rounded-[12px] h-[120px]`}
                  />

                  <div className="flex justify-center">
                    <h3
                      className={`text-[#000] mt-2 text-[12px] sm:text-[16px] font-[600] font-[Lato]  text-center`}
                    >
                      {/* {item?.title} */}
                      {t(`quizGame.AllPlaces.${item?.title}.title`)}
                    </h3>
                  </div>
                  <div className="flex justify-center">
                    {isLocked && (
                      <button
                        disabled
                        className="bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] rounded-[22px] py-2 px-2 text-[12px] my-2"
                      >
                        {/* {unlockDate
                        ? `Unlocked on ${new Date(
                            unlockDate
                          ).toLocaleDateString("en-US", {
                            day: "numeric",
                            month: "long",
                          })}`
                        : "Locked"} */}
                        {!unlockDate
                          ? t("quizGame.Locked")
                          : (() => {
                              const date = new Date(unlockDate);
                              const day = date.getDate();
                              const monthName = date.toLocaleDateString(
                                "en-US",
                                {
                                  month: "long",
                                },
                              );
                              const translatedMonth = t(`month.${monthName}`);

                              return `${t(
                                "quizGame.Unlocked",
                              )} on ${day} ${translatedMonth}`;
                            })()}
                      </button>
                    )}
                  </div>
                  <div className="border border-[#DDDDDD] p-2 rounded-xl flex-grow flex items-center">
                    <p className="text-[10px] sm:text-[12px] text-[#5C5C5C] text-center">
                      {/* {item.description} */}
                      {t(`quizGame.AllPlaces.${item?.title}.description`)}
                    </p>
                  </div>
                </div>
                {!isLocked && (
                  // <img
                  //   src={LockSleepCoach}
                  //   alt="image"
                  //   className="absolute top-[17%] sm:top-[15%] lg:top-[17%] left-1/2 -translate-x-1/2 z-[9] object-cover w-[22px] sm:w-[28px]"
                  // />
                  <img
                    src={LockSleepCoach}
                    alt="image"
                    className="absolute top-[40%] 2xl:top-[30%]  left-1/2 -translate-x-1/2 z-[9] object-cover w-[40px] sm:w-[50px]"
                  />
                )}
              </div>
            </SwiperSlide>
          );
        })}
      </Swiper>
    </div>
  );

  return (
    <>
      {firstLoad ? (
        <ActualDietPlanLoader quizGame={true} />
      ) : (
        <>
          <div className="flex justify-end">
            <button
              className="bg-[#64DDFF] py-[12px] px-6 text-white rounded-full  mb-2 flex items-center gap-2"
              onClick={() => navigate("/quiz-game")}
            >
              <ChevronLeft
                className="w-6 h-6 bg-white rounded-full text-[#4CDBFF] p-[2px]"
                strokeWidth={4}
              />
              {t("quizGame.Return To Dashboard")}
            </button>
          </div>
          <Card className="p-5 !rounded-xl">
            {/* Milestones Section */}
            <div
              className="p-6 w-full rounded-xl text-white bg-cover h-full relative flex flex-col items-center sm:block "
              style={{ backgroundImage: `url(${stepBack})` }}
            >
              {/* <h4 className="text-[16px] font-[Lato] text-white mb-6">
              Your Milestones
            </h4> */}

              {/* Achieved Milestones */}
              {achievedMilestones?.length > 0 &&
                renderMilestoneSlider(
                  achievedMilestones,
                  true,
                  t("quizGame.Your Achieved Milestones"),
                  "milestone-achieved",
                )}

              {/* Unachieved Milestones */}
              {unachievedMilestones?.length > 0 &&
                renderMilestoneSlider(
                  unachievedMilestones,
                  false,
                  t("quizGame.Milestones Yet to Achieve"),
                  "milestone-unachieved",
                )}
            </div>

            {/* Places Section */}
            <div
              className="p-6 w-full rounded-xl text-white bg-cover h-full relative mt-9 flex flex-col items-center sm:block"
              style={{ backgroundImage: `url(${stepBack})` }}
            >
              {achievedPlaces?.length > 0 &&
                renderPlacesSlider(
                  achievedPlaces,
                  true,
                  t("quizGame.Places Unlocked"),
                  "places-achieved",
                )}
              {unachievedPlaces?.length > 0 &&
                renderPlacesSlider(
                  unachievedPlaces,
                  false,
                  t("quizGame.Hidden Destinations"),
                  "places-unachieved",
                )}
            </div>
          </Card>
        </>
      )}
    </>
  );
};

export default AllRanks;
